import { forwardRef, ReactNode } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '../lib/cn';

interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadixCheckbox.Root>, 'children'> {
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
}

export const Checkbox = forwardRef<
  React.ElementRef<typeof RadixCheckbox.Root>,
  CheckboxProps
>(function Checkbox({ label, description, className, id, ...rest }, ref) {
  const root = (
    <RadixCheckbox.Root
      ref={ref}
      id={id}
      className={cn(
        'peer flex h-5 w-5 shrink-0 items-center justify-center rounded-xs border border-border-strong bg-surface',
        'transition-[background-color,border-color,box-shadow] duration-fast ease-enter',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
        'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary',
        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <RadixCheckbox.Indicator className="flex items-center justify-center text-[color:var(--color-on-primary)]">
        {rest.checked === 'indeterminate' ? (
          <Minus className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <Check className="h-3.5 w-3.5" aria-hidden strokeWidth={3} />
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (!label && !description) return root;

  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-2.5 select-none"
    >
      {root}
      <div className="-mt-0.5 min-w-0">
        {label ? (
          <span className="block text-base text-ink leading-ui">{label}</span>
        ) : null}
        {description ? (
          <span className="mt-0.5 block text-sm text-ink-secondary">
            {description}
          </span>
        ) : null}
      </div>
    </label>
  );
});
